import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { t, i18n } = useTranslation();
  const { lang, slug } = useParams();

  // Set language from URL and scroll to top on mount
  useEffect(() => {
    if (lang === 'pt' || lang === 'de' || lang === 'en') {
      i18n.changeLanguage(lang);
    }
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [lang, i18n]);

  const langCode = i18n.language || 'en';
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-40 pb-20 px-4 text-center">
          <h1 className="font-heading text-2xl font-bold text-foreground-950 mb-4">
            Article not found
          </h1>
          <Link
            to={`/${langCode}/`}
            className="text-primary-600 font-medium hover:text-primary-700"
          >
            Back to Home
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const content = article[langCode as 'en' | 'pt' | 'de'] || article.en;
  const related = articles.filter((a) => a.slug !== slug).slice(0, 3);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      {/* Article Header */}
      <header className="pt-24 md:pt-28 pb-10 md:pb-14 px-4 md:px-6 lg:px-8 bg-background-100 border-b border-background-200">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <span className="px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium">Guide</span>
            <span className="text-xs text-foreground-500">{content.date}</span>
          </div>
          <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight mb-4">
            {content.h1}
          </h1>
          <p className="text-sm text-foreground-500">
            {t('blog.updatedLabel')}: {content.updated}
          </p>
        </div>
      </header>

      {/* Article Body */}
      <article className="py-12 md:py-16 px-4 md:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Breadcrumb */}
          <nav className="mb-8 text-sm text-foreground-500">
            <Link to={`/${langCode}/`} className="hover:text-primary-600 transition-colors">{t('nav.home')}</Link>
            <span className="mx-2">/</span>
            <span className="text-foreground-700">CSSBuy Spreadsheet Guide</span>
          </nav>

          {/* Content sections */}
          <div className="prose prose-lg max-w-none">
            {content.sections.map((section, idx) => (
              <div key={idx} className="mb-10 md:mb-12">
                <h2 className="font-heading text-xl md:text-2xl lg:text-3xl font-bold text-foreground-950 mb-4">
                  {section.h2}
                </h2>
                <p className="text-foreground-700 leading-relaxed text-sm md:text-base">
                  {section.content}
                </p>
              </div>
            ))}
          </div>

          {/* Internal link back to Home */}
          <div className="mt-12 p-6 md:p-8 bg-primary-50 rounded-xl border border-primary-200">
            <p className="text-sm md:text-base text-foreground-700 mb-3">
              Explore more resources and organized product categories on our homepage.
            </p>
            <Link
              to={`/${langCode}/`}
              className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors"
            >
              {t('blog.backHome')}
              <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" />
            </Link>
          </div>

          {/* FAQ Section */}
          <div className="mt-16">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-8">
              {t('faq.h2')}
            </h2>
            <div className="space-y-4">
              {content.faq.map((faq, idx) => (
                <div key={idx} className="bg-white rounded-xl border border-background-200 p-5 md:p-6">
                  <h3 className="font-heading text-base md:text-lg font-semibold text-foreground-900 mb-2">
                    {faq.q}
                  </h3>
                  <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                    {faq.a}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Related Guides */}
          <div className="mt-16">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-8">
              {t('blog.relatedTitle')}
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              {related.map((rel) => {
                const relContent = rel[langCode as 'en' | 'pt' | 'de'] || rel.en;
                return (
                  <Link
                    key={rel.slug}
                    to={`/${langCode}/blog/${rel.slug}`}
                    className="group bg-white rounded-xl border border-background-200 overflow-hidden hover:shadow-md transition-all duration-300"
                  >
                    <div className="relative aspect-[16/10] overflow-hidden">
                      <img
                        src={rel.image}
                        alt={relContent.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
                    </div>
                    <div className="p-4">
                      <h3 className="font-heading text-sm font-semibold text-foreground-950 group-hover:text-primary-600 transition-colors line-clamp-2">
                        {relContent.title}
                      </h3>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
}